import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "theme";
import { Mention, MentionsInput } from "react-mentions";
import i18n from "i18n";
import lod_ from "lodash";

/** IMPORT CSS */
import "./mentions.css";

/**
 * React component for text input with user mentions (Slack/Twitter style).
 * Uses react-mentions to provide dynamic mention suggestions and insertion.
 *
 * @component
 * @param {object} props
 * @param {string} props.text - The current text, which may contain mentions in react-mentions format.
 * @param {function} props.onChange - Callback called on every text change (react-mentions event syntax).
 * @param {function} props.setFocus - Callback called on keyUp to indicate focus (e.g., for autosave or UI state management).
 * @returns {JSX.Element} A styled, dark-mode compatible text field with mention suggestions.
 *
 * @example
 * <MentionsComponent
 *   text={text}
 *   onChange={handleChange}
 *   setFocus={setFocus}
 * />
 */
export const MentionsComponent = ({ text, onChange, setFocus }) => {
	const { isDarkMode } = useTheme();
	const [userMention, setUserMention] = useState([]);
	const [skillMention, setSkillMention] = useState([]);

	const { users, groups } = useSelector(state => state.userGroup);

	// Normalize a string for mention searching by removing accents and converting to lowercase
	const normalizeMentionSearch = value =>
		String(value || "")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLowerCase();

	//Initialize user mentions for the MentionsInput component based on the users from the Redux store
	useEffect(() => {
		if (!lod_.isNil(users) && lod_.isArray(users) && !lod_.isEmpty(users)) {
			let usersForMentions = users
				.filter(user => user?.active === true)
				.map(user => ({
					id: user.userID,
					display: `${user.first || ""} ${user.last || ""}`.trim() || user.userID
				}));

			if (lod_.isArray(usersForMentions) && !lod_.isEmpty(usersForMentions)) {
				usersForMentions.sort((a, b) => a.display.localeCompare(b.display));

				setUserMention(usersForMentions);
			} else {
				setUserMention([]);
			}
		} else {
			setUserMention([]);
		}
	}, [users]);

	//Initialize skills mentions for the MentionsInput component based on the skills from the Redux store
	useEffect(() => {
		if (lod_.isArray(groups) && !lod_.isEmpty(groups)) {
			let skillsForMentions = groups.map(skill => ({
				id: skill.code,
				display: skill.name || ""
			}));

			if (lod_.isArray(skillsForMentions) && !lod_.isEmpty(skillsForMentions)) {
				skillsForMentions.sort((a, b) => a.display.localeCompare(b.display));

				setSkillMention(skillsForMentions);
			} else {
				setSkillMention([]);
			}
		} else {
			setSkillMention([]);
		}
	}, [groups]);

	return (
		<MentionsInput
			value={text}
			onChange={onChange}
			onKeyUp={() => setFocus(true)}
			className={`mentions mentions--multiLine notesMentionsInput ${isDarkMode ? "mentions--dark" : ""}`}
			placeholder={i18n.t("COC.mentions.mentionTextAreaPlaceholder")}
			a11ySuggestionsListLabel={"Suggested mentions"}
			style={{
				input: { overflow: "auto" },
				minHeight: 80,

				suggestions: {
					backgroundColor: "transparent",
					top: "auto",
					bottom: "100%",
					marginBottom: "8px"
				}
			}}
		>
			{/* Trigger @ for mentions */}
			<Mention
				trigger="@"
				data={search => {
					const normalizedSearch = normalizeMentionSearch(search || "");
					return userMention
						.filter(u => normalizeMentionSearch(u.display).includes(normalizedSearch))
						.slice(0, 10);
				}}
				markup="@[__display__](__id__)"
				displayTransform={(id, display) => `@${display}`}
				className="mentions__mention"
			/>

			{/* Trigger # for mentions */}
			<Mention
				trigger="#"
				data={search => {
					const normalizedSearch = normalizeMentionSearch(search || "");
					return skillMention
						.filter(u => normalizeMentionSearch(u.display).includes(normalizedSearch))
						.slice(0, 10);
				}}
				markup="#[__display__](__id__)"
				displayTransform={(id, display) => `#${display}`}
				className="mentions__mention"
			/>
		</MentionsInput>
	);
};

/**
 * Transform mentions in the text to HTML span elements with a specific class for styling
 * @param {string} text - The text containing mentions in the format @[display](user:id) or #[display](group:id)
 * @returns {string} - The text with mentions transformed to HTML span elements
 */
export function transformMentionsToHtml(text = "") {
	const userStatus = useSelector(state => state.userStatus);
	let userID = userStatus?.auth?.user?.userID || "";
	let groups = userStatus?.auth?.user?.groups || [];

	if (!text) return "";

	return text.replace(/([@#])\[(.*?)\]\((.*?)\)/g, (_, trigger, display, id) => {
		let isCurrentMention = false;
		if (trigger === "@") {
			isCurrentMention = id === userID;
		} else if (trigger === "#") {
			isCurrentMention = lod_.isArray(groups) && groups.includes(id);
		}

		const className = isCurrentMention
			? "mention-tag mention-tag--current"
			: "mention-tag mention-tag--other";

		return `<span class="${className}" data-id="${id}">${trigger}${display}</span>`;
	});
}
